import { computed, EventEmitter, Injectable, NgZone, signal } from '@angular/core';
import { CoinflipDepositModalComponent } from './coinflip-deposit-modal/coinflip-deposit-modal.component';
import { CoinflipViewModalComponent } from './coinflip-view-modal/coinflip-view-modal.component';
import { CoinflipWonModalComponent } from './coinflip-won-modal/coinflip-won-modal.component';
import { ModalService } from '@app/modal/modal.service';
import { OfferSentModalComponent } from '@app/modals/offer-sent-modal/offer-sent-modal.component';
import { environment } from '@env/environment';
import { generateCoinflipRound } from '@features/demo/coinflip';
import { COINFLIP_SIDE_BLUE, Coinflip, CoinflipReveal } from '@models/coinflip.interface';
import { Item } from '@models/item.interface';
import { SessionService } from '@shared/session.service';
import { SocketService } from '@shared/socket.service';
import { sleep } from '@utils';

@Injectable({
  providedIn: 'root',
})
export class CoinflipService {
  coinflips = signal<Coinflip[]>([]);

  private filteredAndSortedCoinflips = computed(() => {
    return this.coinflips()
      .filter((coinflip) => (this.joinableOnly() ? coinflip.status === 'open' : true))
      .sort((a, b) => (this.sortBySignal() === 'price' ? b.value - a.value : b.start_time - a.start_time));
  });

  myCoinflips = computed(() => {
    const userId = this.sessionService?.profile()?.steamid;
    return this.filteredAndSortedCoinflips().filter((cf) => (cf.host?.steamid === userId || cf.part?.steamid === userId) && !cf.revealed);
  });

  otherCoinflips = computed(() => {
    const userId = this.sessionService?.profile()?.steamid;
    return this.filteredAndSortedCoinflips().filter((cf) => (cf.host?.steamid !== userId && cf.part?.steamid !== userId) || cf.revealed);
  });

  skinsHistory = signal<Coinflip[]>([]);
  historyStats = { blue: 0, red: 0 };

  onCoinflipChange: EventEmitter<Coinflip> = new EventEmitter<Coinflip>();

  totalValue = computed(() => {
    const value = this.coinflips().reduce((acc, c) => acc + ((c?.host?.value ?? 0) + (c?.part?.value ?? 0)), 0);

    return Math.round(value * 100) / 100;
  });

  joinableCount = computed(() => {
    return this.coinflips().filter((c) => c.status === 'open').length;
  });

  coinSide = COINFLIP_SIDE_BLUE;
  sortBySignal = signal<'date' | 'price'>('price');
  joinableOnly = signal<boolean>(false);

  cdnBase = environment.cdnBase;

  constructor(private socketService: SocketService, private sessionService: SessionService, private modalService: ModalService, private ngZone: NgZone) {
    // setTimeout(() => {
    //   this.modalService.closeByComponentTypes([CoinflipDepositModalComponent, OfferSentModalComponent]);
    // }, 10000);

    this.socketService.socket.on('reveal_part_skins', (data: CoinflipReveal) => {
      const currentGame = this.coinflips().find((c) => c.gameid === data.gameid);
      if (currentGame && currentGame.part) {
        currentGame.part = {
          ...currentGame.part,
          skins: data.skins,
          value: data.value,
          reveal: true,
        };
        this.updateCoinflip(currentGame);
      } else {
        console.error(`Game with id ${data.gameid} not found or has no part side`);
      }
    });

    this.socketService.socket.on('coinflip_list', async (data) => {
      this.coinflips.set([]);
      this.updateCoinflips(Object.values(data));
    });

    this.socketService.socket.on('new_coinflip', (data: Coinflip) => {
      this.updateCoinflip(data);

      const userId = this.sessionService?.profile()?.steamid ?? 'unknown';
      const isHost = data.host?.steamid === userId;

      if (isHost) {
        this.modalService.closeByComponentTypes([CoinflipDepositModalComponent, OfferSentModalComponent]);
      }
    });

    this.socketService.socket.on('end_coinflip', (data) => {
      this.updateCoinflip(data);
    });

    this.socketService.socket.on('cf_got_ban', () => {
      this.coinflips.set([]);
    });

    this.socketService.socket.on('joining_coinflip', (data) => {
      this.updateCoinflip(data);
    });

    this.socketService.socket.on('finish_joining', async (data) => {
      await this.startCountdown(data);

      const userId = this.sessionService?.profile()?.steamid ?? 'unknown';

      const isPart = data.part?.steamid === userId;

      if (isPart) {
        if (!this.modalService.isModalOpen(CoinflipViewModalComponent)) {
          this.viewCoinflipById(data.gameid);
        }

        this.modalService.closeByComponentTypes([CoinflipDepositModalComponent, OfferSentModalComponent]);
      }
    });

    this.socketService.socket.on('cancel_join_coinflip', (data) => {
      this.updateCoinflip(data);
    });

    this.socketService.socket.on('coinflip_won', (data: { gameid: string | number; value: number; skins: Item[] }) => {
      const currentGame = this.coinflips().find((game) => game.gameid === data.gameid);

      if (!currentGame) {
        console.error(`coinflip_won for unknown game ${data.gameid}`);
        return;
      }

      const wonSkins = [...(currentGame.host?.skins ?? []), ...(currentGame.part?.skins ?? [])];
      const allSkins = (wonSkins.length ? wonSkins : data.skins ?? []).filter((skin) => skin.is_rake !== 1);

      this.modalService.open(CoinflipWonModalComponent, { ...currentGame, allSkins, value: data.value });
    });

    this.socketService.socket.on('cancel_flip', (data) => {
      this.removeGame(data.id);
    });

    this.socketService.socket.on('remove_flip', (data) => {
      this.removeGame(data.id);
    });

    if (environment.loadDemo) {
      this.coinflips.set([]);
      this.updateCoinflips([
        generateCoinflipRound('open'),
        generateCoinflipRound('open'),
        generateCoinflipRound('joining'),
        generateCoinflipRound('joined', true),
        generateCoinflipRound('finished', true),
        generateCoinflipRound('finished'),
        generateCoinflipRound('finished'),
      ]);
    }

    this.preloadImages();

    this.ngZone.runOutsideAngular(() => {
      setInterval(() => {
        const currentTime = Math.round(new Date().getTime() / 1000);
        const coinflips = this.coinflips();
        let hasChanges = false;
        const updatedCoinflips = [...coinflips];

        for (let i = 0; i < updatedCoinflips.length; i++) {
          const cf = updatedCoinflips[i];

          if (cf.end) {
            const currentTimeSec = Math.round(Date.now() / 1000);
            const newTimer = cf.end - currentTimeSec;

            const nowMs = Date.now();
            const endMs = cf.end * 1000;
            const revealAtMs = endMs + 3000;

            const shouldReveal = !cf.revealed && cf.status === 'finished' && newTimer <= 0 && nowMs >= revealAtMs;

            const nextTimer = Math.max(newTimer, 0);

            if (cf.timer !== nextTimer || shouldReveal) {
              updatedCoinflips[i] = {
                ...cf,
                ...(shouldReveal && { revealed: true }),
              };

              hasChanges = true;

              if (shouldReveal) {
                this.onCoinflipChange.emit(updatedCoinflips[i]);
                this.initCoinsHistory();
              }
            }

            if (cf.status !== 'finished') {
              this.onCoinflipChange.emit(updatedCoinflips[i]);
            }
          }

          if (cf.removing < currentTime) {
            this.removeGame(cf.gameid);
            return;
          }
        }

        if (hasChanges) {
          this.coinflips.set(updatedCoinflips);
        }
      }, 1000);
    });

    // this.ngZone.runOutsideAngular(() => {
    //   setInterval(() => {
    //     const currentTime = Math.round(new Date().getTime() / 1000);
    //     const coinflips = this.coinflips();
    //     let hasChanges = false;
    //     const updatedCoinflips = [...coinflips];

    //     for (let i = 0; i < updatedCoinflips.length; i++) {
    //       const cf = updatedCoinflips[i];

    //       if (cf.end) {
    //         const newTimer = cf.end - currentTime;
    //         const shouldReveal = !cf.revealed && cf.status === 'finished' && newTimer < -5;

    //         if (cf.timer !== newTimer || shouldReveal) {
    //           updatedCoinflips[i] = {
    //             ...cf,
    //             timer: Math.max(newTimer, 0),
    //             ...(shouldReveal && { revealed: true }),
    //           };

    //           hasChanges = true;

    //           if (shouldReveal) {
    //             this.onCoinflipChange.emit(updatedCoinflips[i]);
    //             this.initCoinsHistory();
    //           }
    //         }

    //         if (cf.status !== 'finished') {
    //           this.onCoinflipChange.emit(updatedCoinflips[i]);
    //         }
    //       }

    //       if (cf.removing < currentTime) {
    //         this.removeGame(cf.gameid);
    //         return;
    //       }
    //     }

    //     if (hasChanges) {
    //       this.coinflips.set(updatedCoinflips);
    //     }
    //   }, 500);
    // });

    this.initCoinsHistory();
  }

  private preloadImages() {
    const images = [
      `${this.cdnBase}/coinflip/blue-to-blue-anim.webp`,
      `${this.cdnBase}/coinflip/blue-to-orange-anim.webp`,
      `${this.cdnBase}/coinflip/cf-blue.png`,
      `${this.cdnBase}/coinflip/cf-orange.png`,
      `${this.cdnBase}/coinflip/orange-to-blue-anim.webp`,
      `${this.cdnBase}/coinflip/orange-to-orange-anim.webp`,
      `${this.cdnBase}/favicon.png`,
    ];

    for (const image of images) {
      const img = new Image();
      img.src = image;
    }
  }

  private async startCountdown(game: Coinflip) {
    for (let timer = 4; timer > 0; timer--) {
      this.updateCoinflip({ ...game, timer });
      await sleep(1000);
    }
  }

  updateCoinflips(cfs: Coinflip[]) {
    cfs.forEach((cf) => {
      const updatedCf = {
        ...cf,
      };
      this.updateCoinflip(updatedCf);
    });
  }

  updateCoinflip(coinflip: Coinflip) {
    const coinflips = this.coinflips();
    const index = coinflips.findIndex((c) => c.gameid === coinflip.gameid);

    coinflip.allSkins = [...(coinflip?.host?.skins || []), ...(coinflip?.part?.skins || [])];
    if (coinflip.timer < 0) {
      coinflip.timer = 0;
    }

    if (coinflip.part) {
      coinflip.part.steamid = coinflip.part.steamid.toString();
      if (coinflip.part.steamid === '-1') {
        coinflip.part.name = 'BOT Steve';
        coinflip.part.avatar = `${this.cdnBase}/emojis/stevekappa.png`;
        coinflip.part.level = 99;
      }
    }

    const updatedCoinflips = [...coinflips];
    if (index !== -1) {
      updatedCoinflips[index] = { ...updatedCoinflips[index], ...coinflip };
    } else {
      updatedCoinflips.push(coinflip);
    }

    this.coinflips.set(updatedCoinflips);

    this.onCoinflipChange.emit(coinflip);
  }

  removeGame(id: number) {
    this.coinflips.set(this.coinflips().filter((c) => c.gameid !== id));
  }

  async createCoinflip(items: Item[], side: number, difference: number, pure = false): Promise<{ offerid: number; skins: Item[]; hash: string } | string> {
    return await this.socketService.emit('host_lobby', { skins: items, side, difference, pure: pure ? 1 : 0 });
  }

  async joinCoinflip(coinflip: Coinflip, items: Item[]) {
    return await this.socketService.emit('join_lobby', { skins: items, gameid: coinflip.gameid });
  }

  async callBot(coinflip: Coinflip) {
    return await this.socketService.emit('call_coinflip_bot', { gameid: coinflip.gameid });
  }

  async cancelCoinflip(coinflip: Coinflip) {
    return await this.socketService.emit('cancel_lobby', { gameid: coinflip.gameid });
  }

  async getHistory(sorted?: boolean) {
    const ack = await this.socketService.emit('cf_history');
    const coinflips = Array.isArray(ack) ? (ack as Coinflip[]) : [];
    for (const coinflip of coinflips) {
      coinflip.allSkins = [...(coinflip?.host?.skins || []), ...(coinflip?.part?.skins || [])];
      if (coinflip.status === 'finished') {
        coinflip.revealed = true;
      }
    }

    if (sorted) {
      return coinflips.sort((a, b) => b.start_time - a.start_time);
    }

    return coinflips;
  }

  private initCoinsHistory = async () => {
    try {
      const history = await this.getHistory(true);
      this.calculateCoinflipStats(history);
    } catch {
      // A missing/slow cf_history ack must not take down the coinflip page — stats stay at 0.
    }
  };

  calculateCoinflipStats = (history: Coinflip[]) => {
    const games = history.slice(0, 50);

    this.skinsHistory.set(games);

    const blueCounts = games.filter((val) => val?.winner?.side === COINFLIP_SIDE_BLUE).length;
    const bluePercent = Math.floor((blueCounts / games.length) * 100);
    this.historyStats = {
      blue: isNaN(bluePercent) ? 0 : bluePercent,
      red: 100 - (isNaN(bluePercent) ? 100 : bluePercent),
    };
  };

  viewCoinflipById(id: number) {
    const coinflip = this.coinflips().find((c) => c.gameid === id);
    if (coinflip) {
      this.modalService.open(CoinflipViewModalComponent, coinflip);
    }
  }

  async doubleDown({ id, side }: { id: Coinflip['gameid']; side: number }) {
    return await this.socketService.emit('cf_rehost', { id, side });
  }

  async claimWinnings(id: Coinflip['gameid']) {
    return await this.socketService.emit('cf_claim', id);
  }
}
